import {
  queryOptions,
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  commentAnchor,
  getInterfaceLanguageCatalogue,
  markAllNotificationsReadResultSchema,
  notificationEmailCategoryPatchSchema,
  notificationEmailFrequencyPatchSchema,
  notificationMobilePushCategoryPatchSchema,
  notificationPageSchema,
  notificationPreferencesSchema,
  notificationQuerySchema,
  notificationReadPatchSchema,
  notificationSchema,
  boardSilenceStateSchema,
  mobilePushInstallationListSchema,
  unreadCountSchema,
  type BoardSilenceState,
  type MarkAllNotificationsReadResult,
  type MobilePushInstallationList,
  type NotificationMobilePushCategory,
  type Notification as NotificationRow,
  type NotificationEmailCategory,
  type NotificationEmailFrequency,
  type NotificationPage,
  type NotificationPreferences,
  type NotificationQuery,
  type NotificationReadPatch,
  type InterfaceLanguageCatalogueResponse,
  type InterfaceMessage,
  type UnreadCount,
} from '@molt/shared';
import { del, get, patch, post, put } from './api';
import { interfaceMessage, WEB_INTERFACE_MESSAGES } from './interface-language';
import { queryKeys } from './query-keys';

const PAGE_SIZE = 25;

export interface SetNotificationReadVariables {
  readonly id: string;
  readonly read: boolean;
}

export interface SetNotificationEmailCategoryVariables {
  readonly category: NotificationEmailCategory;
  readonly enabled: boolean;
}

export interface SetNotificationMobilePushCategoryVariables {
  readonly category: NotificationMobilePushCategory;
  readonly enabled: boolean;
}

export interface SetBoardSilenceVariables {
  readonly boardId: string;
  readonly silenced: boolean;
}

export type DesktopNotificationPermission = 'unsupported' | NotificationPermission;
type InterfaceTranslator = (
  key: string,
  values?: Record<string, string | number>,
  count?: number,
) => string;

const DESKTOP_NOTIFICATIONS_KEY = 'molt.desktopNotifications.enabled';
const DESKTOP_SETTING_EVENT = 'molt:desktop-notifications-changed';

export function getNotifications(
  query: Partial<NotificationQuery> = {},
): Promise<NotificationPage> {
  const params = notificationQuerySchema.partial().parse({
    ...query,
    ...(query.unread === undefined ? {} : { unread: String(query.unread) }),
  });
  const search = new URLSearchParams();
  if (params.cursor !== undefined) search.set('cursor', params.cursor);
  if (params.limit !== undefined) search.set('limit', String(params.limit));
  if (params.unread !== undefined) search.set('unread', String(params.unread));
  const suffix = search.toString() === '' ? '' : `?${search.toString()}`;
  return get(`/notifications${suffix}`, notificationPageSchema);
}

export function getUnreadNotificationCount(): Promise<UnreadCount> {
  return get('/notifications/unread-count', unreadCountSchema);
}

export function setNotificationRead(
  id: string,
  body: NotificationReadPatch,
): Promise<NotificationRow> {
  return patch(
    `/notifications/${encodeURIComponent(id)}/read`,
    notificationSchema,
    notificationReadPatchSchema.parse(body),
  );
}

export function getNotificationPreferences(): Promise<NotificationPreferences> {
  return get('/me/notification-preferences', notificationPreferencesSchema);
}

export function setNotificationEmailFrequency(
  emailFrequency: NotificationEmailFrequency,
): Promise<NotificationPreferences> {
  return patch(
    '/me/notification-preferences/email-frequency',
    notificationPreferencesSchema,
    notificationEmailFrequencyPatchSchema.parse({ emailFrequency }),
  );
}

export function setNotificationEmailCategory(
  category: NotificationEmailCategory,
  enabled: boolean,
): Promise<NotificationPreferences> {
  return patch(
    `/me/notification-preferences/email-categories/${encodeURIComponent(category)}`,
    notificationPreferencesSchema,
    notificationEmailCategoryPatchSchema.parse({ enabled }),
  );
}

export function setNotificationMobilePushCategory(
  category: NotificationMobilePushCategory,
  enabled: boolean,
): Promise<NotificationPreferences> {
  return patch(
    `/me/notification-preferences/mobile-push-categories/${encodeURIComponent(category)}`,
    notificationPreferencesSchema,
    notificationMobilePushCategoryPatchSchema.parse({ enabled }),
  );
}

export function getMobilePushInstallations(): Promise<MobilePushInstallationList> {
  return get('/me/mobile-push-installations', mobilePushInstallationListSchema);
}

export function deleteMobilePushInstallation(
  id: string,
): Promise<MobilePushInstallationList> {
  return del(
    `/me/mobile-push-installations/${encodeURIComponent(id)}`,
    mobilePushInstallationListSchema,
  );
}

export function getBoardSilenceState(boardId: string): Promise<BoardSilenceState> {
  return get(`/boards/${encodeURIComponent(boardId)}/silence`, boardSilenceStateSchema);
}

export function setBoardSilenceState(
  boardId: string,
  silenced: boolean,
): Promise<BoardSilenceState> {
  const path = `/boards/${encodeURIComponent(boardId)}/silence`;
  return silenced
    ? put(path, boardSilenceStateSchema)
    : del(path, boardSilenceStateSchema);
}

export function markAllNotificationsRead(): Promise<MarkAllNotificationsReadResult> {
  return post('/notifications/read-all', markAllNotificationsReadResultSchema);
}

export function notificationsQueryOptions(query: Partial<NotificationQuery> = {}) {
  const unread = query.unread === true;
  return queryOptions({
    queryKey: queryKeys.notifications.list(unread),
    queryFn: () => getNotifications({ limit: query.limit ?? PAGE_SIZE, unread }),
  });
}

export function notificationPreferencesQueryOptions() {
  return queryOptions({
    queryKey: queryKeys.notifications.preferences(),
    queryFn: getNotificationPreferences,
  });
}

export function mobilePushInstallationsQueryOptions() {
  return queryOptions({
    queryKey: queryKeys.notifications.mobilePushInstallations(),
    queryFn: getMobilePushInstallations,
  });
}

export function boardSilenceQueryOptions(boardId: string) {
  return queryOptions({
    queryKey: queryKeys.boards.silence(boardId),
    queryFn: () => getBoardSilenceState(boardId),
  });
}

export function unreadNotificationsQueryOptions() {
  return queryOptions({
    queryKey: queryKeys.notifications.unreadCount(),
    queryFn: getUnreadNotificationCount,
  });
}

export function useSetNotificationEmailFrequency(): UseMutationResult<
  NotificationPreferences,
  Error,
  NotificationEmailFrequency
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: setNotificationEmailFrequency,
    onSuccess: (preferences) => {
      queryClient.setQueryData(queryKeys.notifications.preferences(), preferences);
    },
  });
}

export function useSetNotificationEmailCategory(): UseMutationResult<
  NotificationPreferences,
  Error,
  SetNotificationEmailCategoryVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ category, enabled }) =>
      setNotificationEmailCategory(category, enabled),
    onSuccess: (preferences) => {
      queryClient.setQueryData(queryKeys.notifications.preferences(), preferences);
    },
  });
}

export function useSetNotificationMobilePushCategory(): UseMutationResult<
  NotificationPreferences,
  Error,
  SetNotificationMobilePushCategoryVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ category, enabled }) =>
      setNotificationMobilePushCategory(category, enabled),
    onSuccess: (preferences) => {
      queryClient.setQueryData(queryKeys.notifications.preferences(), preferences);
    },
  });
}

export function useDeleteMobilePushInstallation(): UseMutationResult<
  MobilePushInstallationList,
  Error,
  string
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteMobilePushInstallation,
    onSuccess: (installations) => {
      queryClient.setQueryData(
        queryKeys.notifications.mobilePushInstallations(),
        installations,
      );
    },
  });
}

export function useSetBoardSilenceState(): UseMutationResult<
  BoardSilenceState,
  Error,
  SetBoardSilenceVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ boardId, silenced }) => setBoardSilenceState(boardId, silenced),
    onSuccess: (state) => {
      queryClient.setQueryData(queryKeys.boards.silence(state.boardId), state);
    },
  });
}

function updateNotificationInPage(
  page: NotificationPage | undefined,
  updated: NotificationRow,
  unreadOnly: boolean,
): NotificationPage | undefined {
  if (page === undefined) return page;
  const nextRows = page.notifications
    .map((row) => (row.id === updated.id ? updated : row))
    .filter((row) => !unreadOnly || !row.read);
  return { ...page, notifications: nextRows };
}

function unreadDelta(previous: boolean | undefined, next: boolean): number {
  if (previous === undefined || previous === next) return 0;
  if (!previous && next) return -1;
  if (previous && !next) return 1;
  return 0;
}

export function useSetNotificationRead(): UseMutationResult<
  NotificationRow,
  Error,
  SetNotificationReadVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, read }) => setNotificationRead(id, { read }),
    onSuccess: (updated) => {
      let previousRead: boolean | undefined;
      for (const unreadOnly of [false, true]) {
        const key = queryKeys.notifications.list(unreadOnly);
        const page = queryClient.getQueryData<NotificationPage>(key);
        previousRead ??= page?.notifications.find((row) => row.id === updated.id)?.read;
        queryClient.setQueryData<NotificationPage>(key, (current) =>
          updateNotificationInPage(current, updated, unreadOnly),
        );
      }
      const delta = unreadDelta(previousRead, updated.read);
      if (delta !== 0) {
        queryClient.setQueryData<UnreadCount>(
          queryKeys.notifications.unreadCount(),
          (current) =>
            current === undefined
              ? current
              : { count: Math.max(0, current.count + delta) },
        );
      }
    },
  });
}

export function useMarkAllNotificationsRead(): UseMutationResult<
  MarkAllNotificationsReadResult,
  Error,
  void
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => {
      for (const unreadOnly of [false, true]) {
        queryClient.setQueryData<NotificationPage>(
          queryKeys.notifications.list(unreadOnly),
          (current) => {
            if (current === undefined) return current;
            const readPage = {
              ...current,
              notifications: current.notifications.map((row) => ({ ...row, read: true })),
            };
            return unreadOnly
              ? { ...readPage, notifications: [], nextCursor: null }
              : readPage;
          },
        );
      }
      queryClient.setQueryData<UnreadCount>(queryKeys.notifications.unreadCount(), {
        count: 0,
      });
    },
  });
}

function sourceEnglish(
  key: string,
  values: Record<string, string | number> = {},
  count = 0,
): string {
  const message = WEB_INTERFACE_MESSAGES[key];
  if (message === undefined) return key;
  return interfaceMessage(message, values, count);
}

export function notificationBellLabel(
  count: number | undefined,
  t: InterfaceTranslator = sourceEnglish,
): string {
  if (count === undefined || count <= 0) return t('notifications.title');
  return t('notifications.bellLabel', { count: count > 99 ? '99+' : count }, count);
}

export function readDesktopNotificationSetting(): boolean {
  if (typeof window === 'undefined') return false;
  if (typeof window.localStorage?.getItem !== 'function') return false;
  try {
    return window.localStorage.getItem(DESKTOP_NOTIFICATIONS_KEY) === 'true';
  } catch {
    return false;
  }
}

export function writeDesktopNotificationSetting(enabled: boolean): void {
  if (typeof window === 'undefined') return;
  if (typeof window.localStorage?.setItem !== 'function') return;
  try {
    window.localStorage.setItem(DESKTOP_NOTIFICATIONS_KEY, enabled ? 'true' : 'false');
  } catch {
    /* Browser storage can be disabled; the visible permission state still updates. */
  }
}

export function notifyDesktopNotificationSettingChange(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(DESKTOP_SETTING_EVENT));
}

export function subscribeDesktopNotificationSettingChange(
  listener: () => void,
): () => void {
  window.addEventListener(DESKTOP_SETTING_EVENT, listener);
  window.addEventListener('storage', listener);
  return () => {
    window.removeEventListener(DESKTOP_SETTING_EVENT, listener);
    window.removeEventListener('storage', listener);
  };
}

export function desktopNotificationPermission(): DesktopNotificationPermission {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return 'unsupported';
  }
  return window.Notification.permission;
}

function displayName(
  profile: NotificationRow['actor'],
  t: InterfaceTranslator,
): string | null {
  if (profile === null) return null;
  const name = profile.displayName.trim();
  if (name !== '') return name;
  const username = profile.username.trim();
  if (username !== '') return `@${username}`;
  return t('notifications.fallbackMember');
}

function boardOrWorkspace(row: NotificationRow, t: InterfaceTranslator): string {
  return row.board?.title ?? row.workspace?.name ?? t('notifications.fallbackWorkspace');
}

function cardTitle(row: NotificationRow, t: InterfaceTranslator): string {
  return row.card?.title ?? t('notifications.fallbackCard');
}

export function notificationTemplate(
  row: NotificationRow,
  catalogue: InterfaceLanguageCatalogueResponse | null = null,
): InterfaceMessage {
  const resolved = catalogue ?? getInterfaceLanguageCatalogue('en');
  const sourceEventType = sourceEventTypeOf(row);
  if (sourceEventType !== null) {
    const override = resolved.notificationTemplates.sourceEventOverrides[sourceEventType];
    if (override !== undefined) return override;
  }
  return resolved.notificationTemplates.templates[row.type];
}

export function notificationSentence(
  row: NotificationRow,
  catalogue: InterfaceLanguageCatalogueResponse | null = null,
  t: InterfaceTranslator = sourceEnglish,
): string {
  const base = renderNotificationMessage(notificationTemplate(row, catalogue), row, t);
  return row.groupedCount > 1
    ? t(
        'notifications.groupedSuffix',
        { base, count: row.groupedCount - 1 },
        row.groupedCount - 1,
      )
    : base;
}

export function desktopNotificationTitle(
  row: NotificationRow,
  catalogue: InterfaceLanguageCatalogueResponse | null = null,
  t: InterfaceTranslator = sourceEnglish,
): string {
  return notificationSentence(row, catalogue, t);
}

export function notificationHref(row: NotificationRow): string | null {
  /**
   * ⚠ THE ONE TYPE WHOSE DESTINATION IS NOT A BOARD REF, AND IT IS FIRST
   * BECAUSE IT HAS NO REF AT ALL. `change_registration_failing` is written by
   * the delivery sweep with every reference column null — no actor, no card,
   * no board, no workspace — and that is a deliberate ruling in
   * `packages/shared/src/notification.ts`: the registration's SUBJECT board
   * says nothing about the member's integration, so sending the reader there
   * would be a link that answers the wrong question. The consequence, until
   * this arm, was a drawer row telling the member to re-enable something with
   * nowhere to click and an email (always sent — the type maps to no email
   * category, and null means always) carrying the same dead end.
   *
   * `/profile` is where `ChangeRegistrationManager` renders, which is the only
   * surface that lists a member's registrations, says which one is failing and
   * carries the button that resumes it. It is a ROUTE rather than a ref, which
   * is why it cannot come from the row's columns and has to be answered here.
   *
   * ⚠ IT IS ABOVE THE CARD ARM, NOT BELOW IT. A row of this type has no card
   * and no board, so ordering makes no difference today — and it is written
   * first anyway, because a later ref vocabulary that could address a
   * registration would give these rows a board and this arm would silently
   * stop firing from underneath.
   */
  if (row.type === 'change_registration_failing') return '/profile';
  if (row.card !== null) {
    if (row.card.deleted) return null;
    const hash = row.comment !== null ? `#${commentAnchor(row.comment.id)}` : '';
    // `ref` is per recipient (null outside the reference scope or for a
    // deleted card), so `ref ?? shortLink` is the address that resolves for
    // THIS reader — docs/notes/card-keys.md.
    return `/c/${row.card.ref ?? row.card.shortLink}${hash}`;
  }
  if (row.board !== null && !row.board.deleted) return `/b/${row.board.shortLink}`;
  return null;
}

/**
 * What the drawer should put where the inline reply box goes.
 *
 * Three states rather than a boolean, because `FR-NOTIF-009` distinguishes two
 * kinds of "no composer" and asks for different things:
 *
 *   - `'open'` — the composer. A comment-shaped row on a live card the reader
 *     can still reach.
 *   - `'noAccess'` — **a message, never silence.** AC3: *"then they see a 'you
 *     no longer have access' message rather than a blank board."* A reply into
 *     a board the recipient can no longer read could only ever answer 404, so
 *     the composer must go — but rendering nothing satisfies the gate and fails
 *     the acceptance criterion.
 *   - `'none'` — nothing at all. The row is not comment-shaped, or its card is
 *     gone, or its board is closed or deleted: there is no reply to offer and
 *     no access story to tell.
 *
 * ⚠ **`readable` is a fact about THIS READER, and `closed`/`deleted` are facts
 * about the board.** A former member of a live, open board sees
 * `closed: false, deleted: false` and still cannot read it — which is why the
 * two are separate branches here and why `'noAccess'` is reachable at all.
 */
export type InlineReplyState = 'open' | 'noAccess' | 'none';

export function inlineReplyState(row: NotificationRow): InlineReplyState {
  const replyShaped =
    (row.type === 'mentioned' || row.type === 'watched_comment_added') &&
    row.board !== null &&
    row.card !== null &&
    !row.board.deleted &&
    !row.board.closed &&
    !row.card.deleted;
  if (!replyShaped) return 'none';
  return row.board !== null && !row.board.readable ? 'noAccess' : 'open';
}

/**
 * ⚠ **`row.board.readable` is folded in HERE, beside the deleted-board,
 * closed-board and deleted-card branches — it is not a caller's job.** Fan-out 7
 * put that branch in `NotificationRow` and passed it down to
 * `NotificationReply` as a required prop, because this module was outside that
 * chunk's file list; two authors of one fact is how the two drift. The prop is
 * gone and this is the only place the question is asked.
 *
 * Defined as an equality against `inlineReplyState` rather than as a second
 * conjunction, so the two can never disagree about what "can reply" means.
 */
export function canReplyInline(row: NotificationRow): boolean {
  return inlineReplyState(row) === 'open';
}

function renderNotificationMessage(
  message: InterfaceMessage,
  row: NotificationRow,
  t: InterfaceTranslator,
): string {
  const template =
    message.kind === 'text'
      ? message.value
      : row.groupedCount === 1
        ? message.forms.one
        : message.forms.other;
  const values: Record<string, string | number> = {
    actor: displayName(row.actor, t) ?? t('notifications.fallbackActor'),
    board: row.board?.title ?? boardOrWorkspace(row, t),
    card: cardTitle(row, t),
    count: row.groupedCount,
    workspace: row.workspace?.name ?? boardOrWorkspace(row, t),
  };
  return template.replace(/\{([A-Za-z0-9_]+)\}/g, (_match, slot: string) => {
    const value = values[slot];
    if (value === undefined) {
      throw new Error(`notification template "${row.type}" needs a value for {${slot}}`);
    }
    return String(value);
  });
}

/**
 * ⚠ **The `as unknown as { sourceEventType?: unknown }` cast that used to stand
 * here is gone, and it was a hole in `tsc` over a field that is typed.** It was
 * written when `notificationSchema` had no `sourceEventType`, so it silently
 * yielded `undefined` for every row and the drawer described a restored watched
 * card as "archived" while the email for the same row said "restored". Fan-out
 * 7 declared the field (`z.enum(EVENT_TYPE_NAMES).nullable()`), which made the
 * cast start working and left it looking harmless — a cast that reads a real
 * field is still a cast, and it would go on compiling the day the field is
 * renamed. The plain property read cannot.
 *
 * The blank guard is kept: `''` is not a value the enum admits, but an empty
 * override key would look up `sourceEventOverrides['']` and the guard says so
 * once rather than at the call site.
 */
function sourceEventTypeOf(row: NotificationRow): string | null {
  const value = row.sourceEventType;
  return value !== null && value.trim() !== '' ? value : null;
}
